import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function TransientShaperLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Transient Shaper in Music Production: What It Is and How It Works",
      description:
        "Learn what a transient shaper does, how it changes attack and sustain, how it differs from compression, and how it is used on drums, guitars, vocals, and full mixes.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/transient-shaper",
      articleSection: "Audio Education",
      keywords: [
        "transient shaper",
        "what does a transient shaper do",
        "attack and sustain",
        "transient designer",
        "transient shaping",
        "transient shaper vs compressor",
        "drum transient shaper",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "A transient shaper changes the attack and sustain of a sound, making it punchier, snappier, smoother, tighter, or more controlled.",
      icon: <BoltRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It lets you reshape the feel of a sound without relying only on EQ, compression, or level changes.",
      icon: <SpeedRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Drums, percussion, guitars, bass, vocals, room mics, loops, sound design, and sometimes even buses or full mixes.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const transientTypes = [
    {
      name: "Classic Transient Shaper",
      description:
        "Focuses mainly on two broad controls: attack and sustain, making it easy to quickly add punch or tighten decay.",
    },
    {
      name: "Multi-Band Transient Shaper",
      description:
        "Lets you shape attack and sustain in different frequency ranges separately, which is useful for more targeted control.",
    },
    {
      name: "Frequency-Selective Transient Tool",
      description:
        "Targets only certain parts of the spectrum so you can emphasize snap, click, or body without affecting everything equally.",
    },
    {
      name: "Envelope Shaper",
      description:
        "A broader category of tools that reshape how sounds evolve over time, often beyond just simple attack and sustain controls.",
    },
    {
      name: "Transient Emphasizer",
      description:
        "Primarily boosts the leading edge of sounds to increase punch, articulation, or clarity.",
    },
    {
      name: "Transient Softener",
      description:
        "Reduces the impact of the initial hit, making sharp sounds smoother, less spiky, or less aggressive.",
    },
  ];

  const transientConcepts = [
    {
      name: "Transient",
      description:
        "The transient is the initial burst at the beginning of a sound, such as the crack of a snare, the pluck of a string, or the consonant of a vocal.",
    },
    {
      name: "Attack",
      description:
        "Attack control shapes how strong the front edge of a sound feels. More attack usually makes it seem punchier and more immediate.",
    },
    {
      name: "Sustain",
      description:
        "Sustain control shapes the body or tail after the initial hit. More sustain can make a sound feel fuller or longer, while less sustain can tighten it up.",
    },
    {
      name: "Envelope Perception",
      description:
        "A transient shaper changes the envelope of a sound, which strongly affects how energetic, aggressive, soft, tight, or roomy it feels.",
    },
  ];

  const controls = [
    {
      name: "Attack",
      description:
        "Boosts or reduces the front edge of the signal. This is often the main control for adding punch or softening sharp hits.",
    },
    {
      name: "Sustain",
      description:
        "Boosts or reduces the tail or body of the sound. Useful for tightening drums or making room mics and resonant sources feel bigger.",
    },
    {
      name: "Sensitivity / Threshold",
      description:
        "Some transient shapers let you control how strongly the processor reacts to incoming material.",
    },
    {
      name: "Output",
      description:
        "Lets you level-match after shaping the signal so you can judge the change more accurately.",
    },
    {
      name: "Mix",
      description:
        "Blends dry and processed signal together, which is useful for parallel transient shaping.",
    },
    {
      name: "Band Selection",
      description:
        "In multi-band tools, this lets you choose which frequency area you want to shape more precisely.",
    },
  ];

  const commonUses = [
    "Making kick drums hit harder without simply turning them up",
    "Adding crack and definition to snares and percussion",
    "Tightening drum room mics by reducing sustain",
    "Adding pick attack to guitars or bass",
    "Making loops feel punchier and more modern",
    "Softening overly sharp transients that feel clicky or aggressive",
  ];

  const bestPractices = [
    "Start with small moves. Transient shaping can change the feel of a sound very quickly.",
    "Level-match whenever possible so you are judging the envelope change rather than just a louder signal.",
    "Use attack and sustain together. Often the best result comes from balancing both rather than pushing one control too far.",
    "Check the sound in context. Extra attack that sounds exciting in solo can become harsh in the full mix.",
    "Try transient shaping before reaching for heavy compression if your real goal is punch or tightness rather than broader dynamic control.",
  ];

  const relatedLinks = [
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Learn how compression shapes dynamics differently and why transient shapers are often compared with compressors.",
    },
    {
      title: "Limiting",
      to: "/learning/limiting",
      description:
        "See how limiters control peaks, while transient shapers focus more on envelope character and feel.",
    },
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Understand how EQ changes tone, while transient shaping changes the time-based behavior of a sound.",
    },
    {
      title: "Saturation",
      to: "/learning/saturation",
      description:
        "Explore how saturation adds color and density, which can complement the punch and control of transient shaping.",
    },
  ];

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <>
      <Seo
        title="Transient Shaper | Parish Audio Learning"
        description="Learn what a transient shaper does, how it changes attack and sustain, how it differs from compression, and how it is used in music production."
        canonical="https://parishaudio.com/learning/transient-shaper"
        schema={articleSchema}
      />

      <Box ref={topContainer} sx={{ width: "95%", maxWidth: 1100, mx: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Stack spacing={3.25}>
            <Box>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                  maxWidth: 900,
                }}
              >
                Transient Shaper in Music Production: What It Is and How It Works
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  maxWidth: 950,
                }}
              >
                A transient shaper is a processor that changes the envelope of a sound,
                especially its attack and sustain. In practical terms, it can make a drum
                hit feel punchier, a guitar pluck feel sharper, or a room mic feel tighter
                and less washy. It is one of the most direct ways to change how a sound
                feels without necessarily changing its pitch or tonal balance.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  mt: 2,
                  maxWidth: 950,
                }}
              >
                Transient shaping is especially useful when you like the basic tone of a
                sound but want different impact, punch, snap, or decay. That is why it is
                commonly used on drums and percussion, though it can also be valuable on
                guitars, bass, loops, vocals, and even broader mix elements.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 6 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flexShrink: 0 }}>{item.icon}</Box>
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: style.color3,
                            lineHeight: 1.7,
                            fontSize: "0.97rem",
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                What is a transient shaper?
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                A transient shaper is a dynamics-related processor that focuses on the
                shape of a sound over time rather than only its peak level or average
                loudness. It is commonly used to boost or reduce the initial hit of a
                sound and to lengthen or shorten its body afterward.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                This makes transient shaping different from tools like EQ, which changes
                frequency balance, and different from standard compression, which reduces
                dynamic range according to threshold, ratio, attack, and release behavior.
                A transient shaper is often more direct when the goal is simply to make a
                sound hit harder or ring less.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                In many cases, the benefit of a transient shaper is speed and clarity.
                You can often get to the feel you want faster than with a compressor,
                especially when you want more punch without a lot of extra complexity.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                How transient shaping works
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {transientConcepts.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Typography sx={{ color: accent, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                Imagine a snare drum that has a nice tone but does not crack enough at the
                beginning. Increasing attack with a transient shaper can make that initial
                hit feel more defined. Or imagine a kick drum with too much lingering ring.
                Reducing sustain can tighten the tail without needing to gate it or
                completely redesign the sound.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common types of transient shapers
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {transientTypes.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 4 }}>
                      <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Transient shaper vs compressor
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                      Similar goals sometimes, different approach
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A compressor reacts to signal level and reduces dynamic range according
                    to its settings. A transient shaper focuses more directly on the shape
                    of the attack and sustain portions of a sound. Because of that, a
                    transient shaper can often feel quicker and more targeted when the goal
                    is punch, snap, or decay control.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Compressors and transient shapers are not interchangeable in every case.
                    Compression is often better for controlling overall dynamics, glue, or
                    movement. A transient shaper is often better when you want the sound to
                    hit harder, soften, tighten, or bloom differently without necessarily
                    changing everything else about it.
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common transient shaper controls
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {controls.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                            {item.name}
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                How transient shapers are used in mixing
              </Typography>

              <Paper sx={{ p: 2.25, borderRadius: 3, backgroundColor: panelBg }}>
                <Stack spacing={1.35}>
                  {commonUses.map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <LibraryMusicRoundedIcon
                        sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                      />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                One of the most powerful uses of transient shaping is helping a sound fit
                the arrangement. Sometimes a source does not need more EQ or more level. It
                simply needs a different envelope. A punchier front edge can help it cut
                through. A shorter sustain can help it stop cluttering the mix.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                When transient shaping goes too far
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.35}>
                  {[
                    "Too much attack can make drums or picks feel clicky, brittle, or unnaturally sharp.",
                    "Too much sustain can make a sound feel bloated, roomy, or muddy.",
                    "Too much sustain reduction can make a source feel tiny, thin, or lifeless.",
                    "Aggressive shaping across many tracks can make the whole mix feel unnatural or fatiguing.",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <WarningAmberRoundedIcon
                        sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                      />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Best practices for transient shaping
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {bestPractices.map((item, index) => (
                  <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TipsAndUpdatesRoundedIcon
                          sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                        />
                        <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                          {item}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Related learning topics
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {relatedLinks.map((item) => (
                  <Grid item xs={12} md={6} key={item.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>
                          {item.description}
                        </Typography>
                      </Box>

                      <Box>
                        <Button
                          component={RouterLink}
                          to={item.to}
                          variant="outlined"
                          sx={{
                            color: style.color4,
                            borderColor: "rgba(255,255,255,0.18)",
                            "&:hover": {
                              borderColor: accent,
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Final thoughts
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9, mb: 2 }}>
                A transient shaper is one of the most efficient tools for changing how a
                sound feels. It can add punch, reduce ring, soften harsh hits, or make a
                source feel tighter and more controlled without the broader behavior of a
                traditional compressor.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                Used carefully, transient shaping can solve problems quickly and make a mix
                feel more intentional. It is especially powerful when you already like the
                tone of a sound and just want to change the way it hits and fades.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}